import { useEffect, useMemo, useState } from "react";
import { films } from "@/mocks/films";

const navLinks = [
  { label: "Library", href: "#library" },
  { label: "About", href: "#about" },
  { label: "Press", href: "#press" },
  { label: "Contact", href: "#contact" },
];

const LOGO_SRC =
  "https://www.voltagepictures.com/themes/theme-voltage/assets/img/voltage-logo.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return films.filter((f) => f.title.toLowerCase().includes(q)).slice(0, 5);
  }, [query]);

  const closeAll = () => {
    setMenuOpen(false);
    setSearchOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200"
          : "bg-background-50 border-b border-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-6 px-4 md:px-8 h-16 md:h-20">
        <a href="#top" onClick={closeAll} className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <img src={LOGO_SRC} alt="Voltage Pictures" className="h-7 md:h-9 w-auto object-contain" />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="font-label text-xs tracking-[0.22em] uppercase text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:block relative">
            <div
              className={`flex items-center gap-2 rounded-full border border-background-300 bg-background-100 transition-all duration-300 ${
                searchOpen ? "w-64 px-4" : "w-10 px-2.5 justify-center"
              } h-10`}
            >
              <button
                type="button"
                aria-label="Search titles"
                onClick={() => setSearchOpen((v) => !v)}
                className="flex items-center justify-center w-6 h-6 text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer shrink-0"
              >
                <i className={`${searchOpen ? "ri-close-line" : "ri-search-line"} text-lg`}></i>
              </button>
              {searchOpen && (
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search titles"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              )}
            </div>
            {searchOpen && results.length > 0 && (
              <div className="absolute right-0 mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-1.5">
                {results.map((film) => (
                  <a
                    key={film.id}
                    href="#library"
                    onClick={closeAll}
                    className="flex items-center gap-3 rounded-md px-2.5 py-2 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-11 rounded overflow-hidden bg-background-200 shrink-0 flex items-center justify-center">
                      <img src={film.image} alt={film.title} className="w-full h-full object-cover object-top" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-900 truncate">{film.title}</span>
                      <span className="block text-xs text-foreground-500">
                        {film.genre} · {film.year}
                      </span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden flex items-center justify-center w-10 h-10 text-foreground-800 cursor-pointer"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <nav className="flex flex-col">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={closeAll}
                className="font-label text-sm tracking-[0.18em] uppercase text-foreground-800 hover:text-primary-500 transition-colors py-3 border-b border-background-200/70 last:border-0 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}